/**
 * La porte du quiz de prérequis, en machine d'états pure.
 *
 * La règle (celle de l'ancien `usePrerequisiteQuiz`, en live comme hors live) : une activité qui
 * porte un quiz de prérequis (`has_prerequisite_quiz`) reste fermée tant que l'élève n'a pas fait sa
 * seule tentative (`quiz_status` `pending`, ou absent) ; dès qu'une tentative existe, réussie ou
 * ratée, elle s'ouvre. Rater ne ferme rien : le tuteur prend le relais. Une activité finie ne se
 * ferme jamais (l'ancien écran ne posait la porte que sur une activité en cours).
 *
 * Quatre états :
 *  - `locked` : Sam propose « Commencer le quiz » ; `loading` pendant que le quiz se charge ;
 *  - `quiz` : le quiz est ouvert, une conversation de checkpoint (`useCheckpointConversation`), à la
 *    question où l'élève en était (la progression gardée, comme avant) ;
 *  - `passed` : l'activité s'ouvre — pas de quiz, ou une tentative faite ;
 *  - `error` : le quiz n'a pas pu se charger (le réseau, le serveur) ; l'élève le voit, et réessaie.
 *
 * Un quiz introuvable (404) ou sans question ouvre l'activité (`bypassed`, Wajih, 18/10) : il n'y a
 * rien à passer, et l'élève resterait devant une porte que rien n'ouvre. L'incident part dans Bugsnag
 * (le crochet). Avant, la porte restait fermée, avec « Réessayer » qui rechargeait le même quiz vide.
 *
 * Le quiz se lit AVANT l'intro (`reading`) : introuvable, vide ou déjà tenté, l'activité s'ouvre sans
 * que l'élève ait vu « vérifions tes connaissances » ; jouable, il est gardé, et « Commencer le quiz »
 * l'ouvre sans le relire. Une lecture qui échoue (le réseau, le serveur) laisse l'intro, et le toucher
 * relit, comme avant.
 *
 * Le quiz n'est pas une conversation d'IA : ses questions viennent de `GET activities/{id}/quiz`, ses
 * réponses restent dans l'app et partent ensemble, à la dernière (`POST …/quiz/attempt`). Ici, les
 * tours que la conversation montre : la question (`quizQuestionTurn`) et le résultat
 * (`quizResultTurn`), dans la forme que sert le serveur à un checkpoint.
 */
import { type ApiCheckpointConversation } from "../../precall/checkpoint/checkpointConversationMachine"
import { shownChoicesOf } from "../quizChoices"
import { type ApiQuizForReview } from "./quizReview"

/** Ce que la porte lit d'une activité. */
export type PrerequisiteActivity = {
    id?: number | null
    finished_at?: string | null
    has_prerequisite_quiz?: unknown
    quiz_status?: unknown
}

export type ApiQuizQuestion = {
    id: number
    question_text?: string | null
    /** La question avec ses formules en LaTeX ; absente quand elle n'en a pas : `question_text` suffit. */
    display_question_text?: string | null
    options?: unknown[] | null
    /** Les mêmes réponses, rang pour rang, avec leurs formules en LaTeX ; absentes sans formule. */
    display_options?: unknown[] | null
}

/** Les réponses telles que l'élève les voit : leur version à formules quand elle les suit rang pour rang. */
export function shownOptions(question: ApiQuizQuestion | undefined): string[] {
    return shownChoicesOf(
        (question?.options ?? []).map((option) => String(option ?? "")),
        question?.display_options
    )
}

/** La réponse que le serveur juge : le texte simple du même rang que la réponse montrée. */
export function plainAnswer(question: ApiQuizQuestion, shown: string): string {
    const rank = shownOptions(question).indexOf(shown)
    const plain = rank >= 0 ? question.options?.[rank] : undefined
    return plain != null ? String(plain) : shown
}

/** `GET activities/{id}/quiz` (`QuizResource`). */
export type ApiQuiz = {
    id?: number | null
    conversation_id?: number | null
    questions?: ApiQuizQuestion[] | null
    /** La tentative de l'élève : une seule par quiz. */
    latest_attempt?: unknown
}

export type QuizAnswer = { quiz_question_id: number; answer: string }

/** La progression gardée d'un quiz interrompu (`liveMode.quizProgress[conversationId]`). */
export type QuizProgress = { answers?: QuizAnswer[] | null; currentQuestionIndex?: number | null }

/** `POST activities/{id}/quiz/attempt` : la tentative (`data`), et le quiz corrigé (`quiz`) que relit l'élève. */
export type ApiQuizAttemptResponse = {
    data?: { score?: number | null; total?: number | null; percentage?: number | null; passed?: boolean | null } | null
    /** Le `QuizResource` de la tentative : les bonnes réponses et `latest_attempt`, la correction à relire. */
    quiz?: ApiQuizForReview | null
    /** La conversation du quiz telle que le serveur la clôt : son titre et son résumé portent le score, pour le tuteur. */
    conversation?: (NonNullable<ApiCheckpointConversation["conversation"]> & { summary_title?: string | null }) | null
    already_attempted?: boolean
}

export const PREREQUISITE_QUIZ_CONTEXT = "prerequisite_quiz"

/**
 * Le 404 d'un quiz que le masteur a retiré (« Retirer le quiz ») : une app qui a manqué son message
 * l'apprend en lisant le quiz. La porte s'ouvre comme pour un quiz introuvable, mais c'est un geste
 * du masteur, pas un incident à signaler.
 */
export function isRemovedQuizResponse(cause: unknown): boolean {
    const data = (cause as { response?: { data?: { removed?: unknown } } } | null)?.response?.data
    return data?.removed === true
}

/** La règle de la porte. */
export function requiresPrerequisiteQuiz(activity: PrerequisiteActivity | null | undefined): boolean {
    if (activity?.id == null || activity.finished_at) return false
    if (!activity.has_prerequisite_quiz) return false
    return activity.quiz_status == null || activity.quiz_status === "pending"
}

export type PrerequisiteGateStatus = "locked" | "quiz" | "passed" | "error"

export type PrerequisiteGateState = {
    activityId: number | null
    status: PrerequisiteGateStatus
    /** Le quiz se charge (« Commencer le quiz » touché). */
    loading: boolean
    /** Le quiz se lit avant l'intro : rien ne se montre encore de la porte. */
    reading: boolean
    /** La lecture d'avant l'intro est faite (réussie ou non) : elle ne se refait pas. */
    read: boolean
    /** La tentative est faite (réussie ou ratée) : l'activité s'ouvre à la fermeture du quiz. */
    attempted: boolean
    quiz: ApiQuiz | null
    /** Où reprendre : la question, et les réponses déjà données. */
    startIndex: number
    answers: QuizAnswer[]
    /** Le premier tour de la conversation à ouvrir. */
    conversation: ApiCheckpointConversation | null
    /** Chaque ouverture du quiz monte une conversation neuve. */
    session: number
    /** Le quiz est introuvable ou vide : l'activité s'est ouverte sans lui, et le reste à la relecture. */
    bypassed: boolean
}

export type PrerequisiteGateEvent =
    | { type: "activity"; activity: PrerequisiteActivity | null }
    /** La lecture du quiz avant l'intro. */
    | { type: "readRequested" }
    | { type: "startRequested" }
    | { type: "quizLoaded"; quiz: ApiQuiz | null; saved: QuizProgress | null }
    /** `missing` : le serveur dit que le quiz n'existe pas (404). Sinon, le réseau ou le serveur. */
    | { type: "quizLoadFailed"; missing?: boolean }
    | { type: "attemptSubmitted" }
    /** Le serveur dit que le masteur a retiré le quiz (la tentative refusée d'une app qui a manqué son message). */
    | { type: "quizRemoved" }
    | { type: "quizClosed" }

export function initialGateState(activity: PrerequisiteActivity | null | undefined): PrerequisiteGateState {
    return {
        activityId: activity?.id ?? null,
        status: requiresPrerequisiteQuiz(activity) ? "locked" : "passed",
        loading: false,
        reading: false,
        read: false,
        attempted: false,
        quiz: null,
        startIndex: 0,
        answers: [],
        conversation: null,
        session: 0,
        bypassed: false,
    }
}

/** Les questions d'un quiz qui ont un id : celles qui se jouent. */
export const questionsOf = (quiz: ApiQuiz | null | undefined): ApiQuizQuestion[] =>
    (quiz?.questions ?? []).filter((question) => question?.id != null)

/** Un quiz se passe s'il a sa conversation et au moins une question. */
export function isPlayableQuiz(quiz: ApiQuiz | null | undefined): boolean {
    return quiz?.conversation_id != null && questionsOf(quiz).length > 0
}

/** La question `index`, en tour de checkpoint : Sam la pose, ses réponses sont les options. */
export function quizQuestionTurn(quiz: ApiQuiz, index: number, answered: number): ApiCheckpointConversation {
    const question = questionsOf(quiz)[index]
    return {
        conversation: {
            id: quiz.conversation_id ?? undefined,
            status: "ongoing",
            trigger_context: PREREQUISITE_QUIZ_CONTEXT,
        },
        agent_message: {
            id: `quiz_question_${question?.id}`,
            conversation_id: quiz.conversation_id ?? undefined,
            body: question?.display_question_text || question?.question_text || "",
            proposed_responses: shownOptions(question).map((option) => ({ body: option })),
        },
        student_responses_count: answered,
    }
}

/**
 * Le résultat. Réussi : le mot de l'ancien écran. Raté : le tandem, ton masteur et Sam qui t'aident à
 * finir (l'ancien ne nommait que le masteur). La conversation est finie : réussi ou à reprendre avec le tuteur.
 */
export function quizResultTurn(quiz: ApiQuiz, response: ApiQuizAttemptResponse | null): ApiCheckpointConversation {
    const attempt = response?.data ?? {}
    const passed = attempt.passed === true
    const score = attempt.score ?? 0
    const total = attempt.total ?? questionsOf(quiz).length
    const percentage = Math.round(attempt.percentage ?? (total > 0 ? (score / total) * 100 : 0))
    const body = passed
        ? `Bravo ! Tu as réussi le quiz avec ${score}/${total} (${percentage}%).\n\nTu peux maintenant commencer l'exercice ! 🎉`
        : `Tu as obtenu ${score}/${total} (${percentage}%).\n\nPas de souci, ton masteur et Sam t'aident à finir l'exercice. 💪`
    return {
        conversation: {
            id: quiz.conversation_id ?? undefined,
            // Les deux statuts que le serveur pose sur la conversation du quiz (`QuizService::submitAttempt`).
            status: passed ? "ended" : "tutor_needed",
            trigger_context: PREREQUISITE_QUIZ_CONTEXT,
        },
        agent_message: {
            id: "quiz_result",
            conversation_id: quiz.conversation_id ?? undefined,
            body,
            proposed_responses: [],
        },
        student_responses_count: questionsOf(quiz).length,
    }
}

/** Où reprendre un quiz interrompu : une progression qui ne tient plus (quiz changé) repart du début. */
function resumePoint(quiz: ApiQuiz, saved: QuizProgress | null): { index: number; answers: QuizAnswer[] } {
    const total = questionsOf(quiz).length
    const index = saved?.currentQuestionIndex ?? 0
    const answers = Array.isArray(saved?.answers) ? (saved?.answers as QuizAnswer[]) : []
    if (!Number.isInteger(index) || index < 0 || index >= total || answers.length !== index) {
        return { index: 0, answers: [] }
    }
    return { index, answers }
}

/** Une lecture du quiz qui a répondu, quelle que soit sa réponse. */
const READ_DONE = { loading: false, reading: false, read: true } as const

/** Le masteur a retiré le quiz : l'élève passe à l'énoncé, même au milieu du quiz — pas après sa tentative. */
function released(state: PrerequisiteGateState): PrerequisiteGateState {
    if (state.status === "passed" || state.attempted) return state
    return { ...state, status: "passed", loading: false, reading: false, conversation: null }
}

export function prerequisiteGateReducer(
    state: PrerequisiteGateState,
    event: PrerequisiteGateEvent
): PrerequisiteGateState {
    switch (event.type) {
        case "activity": {
            const id = event.activity?.id ?? null
            if (id !== state.activityId) return initialGateState(event.activity)
            // Le masteur a retiré le quiz (« Retirer le quiz ») : seul ce geste rabat le drapeau, donc
            // l'élève est délivré, même au milieu du quiz, et voit l'énoncé. Pas après sa tentative : la
            // correction qu'il lit reste.
            if (event.activity?.has_prerequisite_quiz === false) return released(state)
            // La même activité relue (la todo rafraîchie) : un quiz ouvert ou une tentative faite tiennent,
            // même si la relue est encore d'avant la tentative.
            // Un quiz introuvable ou vide aussi : relire l'activité ne referme pas la porte qu'il a ouverte.
            if (state.status === "quiz" || state.attempted || state.bypassed) return state
            const requires = requiresPrerequisiteQuiz(event.activity)
            if (!requires && state.status !== "passed") return { ...state, status: "passed", loading: false }
            if (requires && state.status === "passed") return { ...state, status: "locked" }
            return state
        }
        case "readRequested":
            if (state.status !== "locked" || state.loading || state.reading || state.read) return state
            return { ...state, reading: true }
        case "startRequested":
            if (state.status !== "locked" && state.status !== "error") return state
            if (state.loading) return state
            return { ...state, status: "locked", loading: true }
        case "quizLoaded": {
            if (!state.loading && !state.reading) return state
            const quiz = event.quiz
            // Une tentative existe déjà (la todo n'était pas encore relue) : l'activité s'ouvre.
            if (quiz?.latest_attempt != null) return { ...state, ...READ_DONE, status: "passed", attempted: true }
            // Rien à passer : l'activité s'ouvre.
            if (quiz == null || !isPlayableQuiz(quiz))
                return { ...state, ...READ_DONE, status: "passed", bypassed: true }
            // Lu avant l'intro : il est gardé, l'intro se montre, et « Commencer le quiz » l'ouvrira.
            if (!state.loading) return { ...state, ...READ_DONE, quiz }
            const { index, answers } = resumePoint(quiz, event.saved)
            return {
                ...state,
                ...READ_DONE,
                status: "quiz",
                quiz,
                startIndex: index,
                answers,
                conversation: quizQuestionTurn(quiz, index, answers.length),
                session: state.session + 1,
            }
        }
        case "quizLoadFailed": {
            if (!state.loading && !state.reading) return state
            if (event.missing === true) return { ...state, ...READ_DONE, status: "passed", bypassed: true }
            // La lecture d'avant l'intro a échoué : l'intro se montre, et le toucher relira.
            if (!state.loading) return { ...state, ...READ_DONE }
            return { ...state, ...READ_DONE, status: "error" }
        }
        case "attemptSubmitted":
            return { ...state, attempted: true }
        case "quizRemoved":
            return released(state)
        case "quizClosed":
            if (state.status !== "quiz") return state
            return { ...state, status: state.attempted ? "passed" : "locked", conversation: null }
        default:
            return state
    }
}

/** La porte arrête l'activité : le chrono, et la page sous le voile. */
export function gateBlocks(state: PrerequisiteGateState): boolean {
    return state.status !== "passed"
}
